import { useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { studentMessagingApi } from "../../api/student-messaging.api";
import { errorText, useCommand } from "./command";
import { BackButton } from "./parts";
import { Rephrase } from "./Rephrase";
import { STUDENT_MAX_BODY, studentErrorMessage } from "./student-shared";

/**
 * Message à tous les élèves non archivés : déposé dans la boîte du bureau de chacun, au nom de la
 * personne qui écrit, avec une notification. Envoi confirmé par le nombre d'élèves ; une même
 * saisie renvoyée ne dépose rien deux fois.
 */
export function BroadcastToStudents({ onDone }: { onDone: () => void }) {
  const [body, setBody] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [result, setResult] = useState<number | null>(null);
  const command = useCommand(),
    lock = useRef(false);
  const recipients = useQuery({
    queryKey: ["student-messaging", "broadcast-recipients"],
    queryFn: studentMessagingApi.broadcastRecipients,
  });
  const count = recipients.data?.count ?? 0;
  const plural = (n: number) => `${n} élève${n > 1 ? "s" : ""}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (lock.current || !text || !count) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      const sent = await studentMessagingApi.broadcast(command(text), text);
      setResult(sent.recipients);
    } catch (err) {
      setError(studentErrorMessage(err, errorText(err)));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="messaging-form messaging-new"
      aria-label="Message à tous les élèves"
    >
      <header className="messaging-conv-head">
        <BackButton onBack={onDone} />
        <div className="messaging-conv-title">
          <h2>Message à tous les élèves</h2>
          <p className="messaging-conv-sub">
            {recipients.isPending
              ? "Recherche des élèves…"
              : recipients.isError
                ? "Destinataires indisponibles."
                : `${plural(count)} non archivé${count > 1 ? "s" : ""} le recevr${count > 1 ? "ont" : "a"}.`}
          </p>
        </div>
      </header>
      {result !== null ? (
        <>
          <p role="status" className="messaging-student-notice">
            Message envoyé à {plural(result)}. Chacun le lit dans sa boîte du
            secrétariat et reçoit une notification ; ses réponses arrivent au
            secrétariat.
          </p>
          <div className="messaging-form-actions">
            <button className="btn btn--primary" type="button" onClick={onDone}>
              Fermer
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="messaging-student-notice">
            Le message est déposé dans la boîte du secrétariat de chaque élève,
            à votre nom. Chaque élève ne voit que son échange.
          </p>
          <Rephrase text={body} disabled={busy} onUse={setBody} />
          <fieldset disabled={busy}>
            <label>
              Message
              <textarea
                value={body}
                maxLength={STUDENT_MAX_BODY}
                rows={6}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Votre message à tous les élèves…"
              />
            </label>
          </fieldset>
          {error && <p role="alert">{error}</p>}
          <div className="messaging-form-actions">
            <button
              className="btn btn--ghost"
              type="button"
              onClick={onDone}
              disabled={busy}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={busy || !body.trim() || !count}
            >
              {busy ? "Envoi…" : `Envoyer à ${plural(count)}`}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
